import { Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirstMountState } from 'react-use';
import { Notification } from 'ui-components/dist/Notification';
import authApi from '~/authApi';
import { ContentDetailModal } from '~/components/ContentDetailModal';
import { ContentSearchFilter } from '~/components/ContentSearchFilter';
import { ContentSearchSkeleton } from '~/components/ContentSearchSkeleton';
import Spinner from '~/components/Spinner';
import { FEATURE_FLAGS, USER_ROLES, VIEW_OPTIONS } from '~/constants';
import {
  CONTENT_SEARCH_PARAMS,
  CONTENT_SEARCH_ROSTER,
  useContentSearch,
} from '~/hooks/useContentSearch';
import { useCreators } from '~/hooks/useCreators';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import {
  CONTENT_SEARCH_WILDCARD_REMINDER_DISMISSED,
  usePersistedState,
} from '~/hooks/usePersistedState';
import { ISearchContentResult, PAGE_SIZE } from '~/searchApi';
import { selectTalentManager } from '~/selectors/talent-manager';

import {
  ContentSearchFilterProvider,
  useContentSearchFilterContext,
} from './ContentSearchFilterContext';
import { SearchContentComponent } from './SearchContent.component';
import { getQueryParams } from './SearchContent.util';
import { ContentSearchSubnavbar } from './components/ContentSearchFilter/ContentSearchSubnavbar/ContentSearchSubnavbar';
import { useSearchContentParams } from './useSearchContentParams';
import { useSearchContentStore } from './useSearchContentStore';

interface Props {
  pathname: string;
}

const SearchContentContainerInner: React.FC = () => {
  const user = authApi.user;
  const manager = useSelector(selectTalentManager);
  const router = useRouter();
  const isFirstMount = useFirstMountState();
  const {
    onChange,
    isGlobalContentFiltersEnabled,
    globalContentFiltersFFStatus,
  } = useContentSearchFilterContext();

  const isStickyHeaderEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamContentSearchStickyHeader) === 'enabled';

  const currentContentId = router.query.contentId as string;

  const [isWildcardDismissed, setIsWildcardDismissed] = usePersistedState(
    CONTENT_SEARCH_WILDCARD_REMINDER_DISMISSED,
    false,
  );

  const [isWildcardNotificationVisible, setIsWildcardNotificationVisible] =
    useState(false);

  const { contents, isFetching, query, setQuery, totalItems, lastPage } =
    useContentSearch({
      defaultQuery: {
        [CONTENT_SEARCH_PARAMS.ads]: false,
        [CONTENT_SEARCH_PARAMS.page_size]: PAGE_SIZE,
        [CONTENT_SEARCH_PARAMS.roster]: manager
          ? CONTENT_SEARCH_ROSTER.agency
          : undefined,
        [CONTENT_SEARCH_PARAMS.view]: VIEW_OPTIONS.grid,
      },
      isEnabled: Boolean(user?.id) && !isFirstMount,
    });

  const { query: search, view } = getQueryParams(router.query);

  useEffect(() => {
    setIsWildcardNotificationVisible(
      !!search &&
        search.length > 2 &&
        !search.endsWith('*') &&
        !isWildcardDismissed,
    );
  }, [search, isWildcardDismissed]);

  const handleWildcardClick = useCallback(
    () => onChange({ query: search + '*' }),
    [onChange, search],
  );

  const handleModalClose = useCallback(() => {
    const query = router.query;
    delete query.contentId;
    router.push(
      {
        query,
      },
      undefined,
      { shallow: true },
    );
  }, [router]);

  const contentSearchParams = useSearchContentParams();

  useEffect(() => {
    setQuery(contentSearchParams);
  }, [contentSearchParams, setQuery]);

  const handlePagination = useCallback(
    (page: number) => onChange({ page: page.toString() }),
    [onChange],
  );

  const handleClear = useCallback(() => onChange({}), [onChange]);

  const handleClickItem = useCallback(
    (content: ISearchContentResult) => {
      router.push(
        {
          query: {
            ...router.query,
            contentId: content.contentId,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const handleNavigation = useCallback(
    (direction: string) => {
      let currentIndex = contents.findIndex(
        ({ contentId }) => contentId === currentContentId,
      );

      if (direction === 'next') {
        currentIndex++;
      }

      if (direction === 'previous') {
        currentIndex--;
      }

      if (currentIndex >= contents.length) {
        currentIndex = 0;
      }

      if (currentIndex < 0) {
        currentIndex = contents.length - 1;
      }

      router.push({
        query: {
          ...router.query,
          contentId: contents[currentIndex].contentId,
        },
      });
    },
    [currentContentId, contents, router],
  );

  return (
    <Flex
      alignItems="stretch"
      flexDirection="column"
      minHeight="100%"
      flex="1 0 100%"
    >
      {globalContentFiltersFFStatus !== 'loading' ? (
        isStickyHeaderEnabled ? (
          <div className="relative top-0 z-10 mb-2 bg-neutral-0 pb-4 pt-6 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-[-8px] after:z-[11] after:h-[8px] after:bg-gradient-to-b after:from-[var(--wb-color-neutral-0)] after:to-[var(--wb-color-white-alpha-0)] after:content-[''] mac:sticky">
            {isGlobalContentFiltersEnabled ? (
              <ContentSearchSubnavbar />
            ) : (
              <ContentSearchSubnavbarDeprecated />
            )}
          </div>
        ) : (
          <div className="py-6">
            {isGlobalContentFiltersEnabled ? (
              <ContentSearchSubnavbar />
            ) : (
              <ContentSearchSubnavbarDeprecated />
            )}
          </div>
        )
      ) : null}
      {isWildcardNotificationVisible ? (
        <Notification
          isInline={true}
          marginBottom={4}
          variant="info"
          onClickClose={() => setIsWildcardNotificationVisible(false)}
          secondaryAction={{
            onClick: () => setIsWildcardNotificationVisible(false),
            children: 'Close',
          }}
          primaryAction={{
            onClick: () => setIsWildcardDismissed(true),
            children: 'Stop showing me this',
          }}
        >
          <Text as="span">
            Not the content you were looking for? Expand results with wildcard
            search, eg.{' '}
          </Text>
          <Text
            as="b"
            color="blue.400"
            onClick={handleWildcardClick}
            cursor="pointer"
            role="button"
          >
            {decodeURIComponent(query.query ?? '')}*
          </Text>
        </Notification>
      ) : null}
      {isFetching ? (
        view === VIEW_OPTIONS.grid ? (
          <ContentSearchSkeleton items={12} />
        ) : (
          <Spinner centered />
        )
      ) : (
        <Flex
          flex="1 0 100%"
          flexDirection="column"
          justifyContent="stretch"
          minHeight="100%"
        >
          <SearchContentComponent
            clear={handleClear}
            onClickItem={handleClickItem}
            onPagination={handlePagination}
            page={query.page ?? '1'}
            platforms={query.platform ?? []}
            query={query.query}
            results={contents}
            totalItems={totalItems ?? contents.length}
            lastPage={lastPage}
            view={(view as VIEW_OPTIONS) ?? VIEW_OPTIONS.grid}
          />
        </Flex>
      )}

      <ContentDetailModal
        isOpen={Boolean(router.query.contentId)}
        onClose={handleModalClose}
        onNavigation={handleNavigation}
      />
    </Flex>
  );
};

export const SearchContent: React.FC<Props> = memo(({ pathname }) => {
  return (
    <ContentSearchFilterProvider pathname={pathname}>
      <SearchContentContainerInner />
    </ContentSearchFilterProvider>
  );
});

/**
 * @deprecated
 */
function ContentSearchSubnavbarDeprecated() {
  const user = authApi.user;
  const manager = useSelector(selectTalentManager);
  const { onChange } = useContentSearchFilterContext();
  const router = useRouter();
  const { view } = getQueryParams(router.query);
  const filters = useSearchContentParams();
  const { creators } = useCreators();
  const isManagerAccount = Boolean(useSelector(selectTalentManager));
  const { contents } = useContentSearch({
    defaultQuery: {
      [CONTENT_SEARCH_PARAMS.ads]: false,
      [CONTENT_SEARCH_PARAMS.page_size]: PAGE_SIZE,
      [CONTENT_SEARCH_PARAMS.roster]: manager
        ? CONTENT_SEARCH_ROSTER.agency
        : undefined,
      [CONTENT_SEARCH_PARAMS.view]: VIEW_OPTIONS.grid,
    },
    isEnabled: Boolean(user?.id),
  });
  const { hasFiltersApplied } = useSearchContentStore();
  const disableHeader =
    !creators.length &&
    !contents.length &&
    !hasFiltersApplied &&
    isManagerAccount;

  return (
    <ContentSearchFilter
      filters={{
        view,
        ...filters,
      }}
      onChange={onChange}
      enabled={{
        [CONTENT_SEARCH_PARAMS.roster]: Boolean(manager?.agency?.id),
        [CONTENT_SEARCH_PARAMS.view]: true,
        [CONTENT_SEARCH_PARAMS.creator]: user.roles.includes(
          USER_ROLES.talentManager,
        ),
        [CONTENT_SEARCH_PARAMS.ads]: true,
        [CONTENT_SEARCH_PARAMS.postedAfter]: true,
      }}
      order={{
        [CONTENT_SEARCH_PARAMS.roster]: 0,
        [CONTENT_SEARCH_PARAMS.ads]: 1,
        [CONTENT_SEARCH_PARAMS.query]: 2,
        [CONTENT_SEARCH_PARAMS.sortBy]: 3,
        [CONTENT_SEARCH_PARAMS.view]: 4,
        [CONTENT_SEARCH_PARAMS.platform]: 5,
        [CONTENT_SEARCH_PARAMS.creator]: 6,
        [CONTENT_SEARCH_PARAMS.postedAfter]: 7,
      }}
      disabledComponents={{
        [CONTENT_SEARCH_PARAMS.roster]: disableHeader,
        [CONTENT_SEARCH_PARAMS.ads]: disableHeader,
        [CONTENT_SEARCH_PARAMS.query]: disableHeader,
        [CONTENT_SEARCH_PARAMS.sortBy]: disableHeader,
        [CONTENT_SEARCH_PARAMS.view]: disableHeader,
        [CONTENT_SEARCH_PARAMS.platform]: disableHeader,
        [CONTENT_SEARCH_PARAMS.creator]: disableHeader,
        [CONTENT_SEARCH_PARAMS.postedAfter]: disableHeader,
      }}
    />
  );
}
